import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { defaultFilters } from './filtersDefinitions';
import type { Filters } from './filtersDefinitions';

export interface FiltersContextType {
	filters: Filters;
	resetFilters: () => void;
	setFilter: (key: keyof Filters, value) => void;
}

export const FiltersContext = createContext<FiltersContextType>({
	filters: {},
	resetFilters: () => null,
	setFilter: () => null,
});

export const useFiltersContext = () => useContext(FiltersContext);

interface FiltersProviderProps {
	children: ReactNode;
}

export const FiltersProvider = ({ children }: FiltersProviderProps) => {
	const [filters, setFilters] = useState<Filters>(defaultFilters);

	const resetFilters = useCallback(() => {
		setFilters(defaultFilters);
	}, []);

	const setFilter = useCallback((key: keyof Filters, value: any) => {
		setFilters((prev: Filters) => ({
			...prev,
			[key]: value,
		}));
	}, []);

	const filtersContextValue = useMemo(
		() => ({
			filters,
			resetFilters,
			setFilter,
		}),
		[filters, resetFilters, setFilter],
	);

	return <FiltersContext.Provider value={filtersContextValue}>{children}</FiltersContext.Provider>;
};
